'use client';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Button from '@/components/ui/Button';
import ChipView from '@/components/ui/ChipView';
import ProductCarousel from '@/components/ui/ProductCarousel';
import { useProductsByCategory } from '@/hooks/useProducts';
import { PRESTASHOP_CONFIG } from '@/lib/prestashop-ws';
import { ProductBase } from '@/types/product';
import { BreadcrumbMenu } from '@/types/menu';

interface GlutenFreeSectionProps {
  className?: string;
  title?: string;
  subtitle?: string;
  products?: ProductBase[];
}

const GlutenFreeSection: React.FC<GlutenFreeSectionProps> = ({
  className = '',
  title = 'ELIGE TU PRÓXIMO CAPRICHO ITALIANO',
  subtitle = 'Explora nuestras Novedades, aprovecha las Promociones especiales\n y disfruta del sabor auténtico con nuestra selección Sin Gluten.',
  products = [],
}) => {
  const [selectedChip, setSelectedChip] = useState('Novedades');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const breadcrumbMenuGlutenFree: BreadcrumbMenu = {
    menuLevel1: {
      id: 0,
      description: '',
      level: 0,
      parent: 0,
      name: 'Sin Gluten',
      slug: 'sin-gluten',
      pageNumbers: 0,
      url: '',
    },
  };

  const breadcrumbMenuNews: BreadcrumbMenu = {
    menuLevel1: {
      id: 0,
      description: '',
      level: 0,
      parent: 0,
      name: 'Novedades',
      slug: 'novedades',
      pageNumbers: 0,
      url: '',
    },
  };

  const breadcrumbMenuPromociones: BreadcrumbMenu = {
    menuLevel1: {
      id: 0,
      description: '',
      level: 0,
      parent: 0,
      name: 'Promociones',
      slug: 'promociones',
      pageNumbers: 0,
      url: '',
    },
  };

  // aplicar una logica ára breadcrumbMenu ára cuando cambie el selectedChip
  const [breadcrumbMenu, setBreadcrumbMenu] = useState<BreadcrumbMenu>(breadcrumbMenuGlutenFree);
  useEffect(() => {
    setBreadcrumbMenu(selectedChip === 'Novedades' ? breadcrumbMenuNews : selectedChip === 'Promociones' ? breadcrumbMenuPromociones : breadcrumbMenuGlutenFree);
  }, [selectedChip]);

  const { data: productsNovedades, isLoading: isLoadingNovedades } = useProductsByCategory(
    [PRESTASHOP_CONFIG.NOVEDADES_ID]
  );
  const { data: productsPromociones, isLoading: isLoadingPromociones } = useProductsByCategory(
    [PRESTASHOP_CONFIG.PROMOCIONES_ID]
  );
  const { data: productsSinGluten, isLoading: isLoadingSinGluten } = useProductsByCategory(
    [PRESTASHOP_CONFIG.GLUTEN_FREE_ID]
  );

  // Determinar qué productos mostrar según el chip seleccionado
  const getProductsToShow = () => {
    switch (selectedChip) {
      case 'Novedades':
        return productsNovedades?.data ?? [];
      case 'Promociones':
        return productsPromociones?.data ?? [];
      case 'Sin Gluten':
        return productsSinGluten?.data ?? [];
      default:
        return [];
    }
  };

  const getLoading = () => {
    switch (selectedChip) {
      case 'Novedades':
        return isLoadingNovedades;
      case 'Promociones':
        return isLoadingPromociones;
      case 'Sin Gluten':
        return isLoadingSinGluten;
      default:
        return false;
    }
  };
  const isLoading = getLoading();


  // Si está cargando, mostrar skeleton loader
  if (isLoading) {
    return (
      <div data-component-id="src/components/sections/GlutenFreeSection.tsx:115:6" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="115" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className}`}>
        <div data-component-id="src/components/sections/GlutenFreeSection.tsx:116:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="116" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-2%20text-left%22%7D" className="mb-2 text-left">
          <h2 data-component-id="src/components/sections/GlutenFreeSection.tsx:117:10" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="117" data-component-file="GlutenFreeSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-%5B40px%5D%20leading-%5B48px%5D%20text-global-1%20mb-2%22%7D" className="font-tt-norms-extra-black text-[40px] leading-[48px] text-global-1 mb-2">
            {title}
          </h2>
          <div data-component-id="src/components/sections/GlutenFreeSection.tsx:120:10" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="120" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded%20px-4%20inline-block%20mb-6%22%7D" className="bg-global-9 rounded px-4 inline-block mb-6">
            <span data-component-id="src/components/sections/GlutenFreeSection.tsx:121:12" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="121" data-component-file="GlutenFreeSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-%5B22px%5D%20leading-%5B26px%5D%20text-global-1%20whitespace-pre-line%22%7D" className="font-tt-norms-medium text-[22px] leading-[26px] text-global-1 whitespace-pre-line">
              {subtitle}
            </span>
          </div>
        </div>
        <div data-component-id="src/components/sections/GlutenFreeSection.tsx:126:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="126" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20gap-x-4%20mb-4%20justify-start%22%7D" className="flex flex-row gap-x-4 mb-4 justify-start">
          <ChipView data-component-id="src/components/sections/GlutenFreeSection.tsx:127:10" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="127" data-component-file="GlutenFreeSection.tsx" data-component-name="ChipView" data-component-content="%7B%22elementName%22%3A%22ChipView%22%2C%22className%22%3A%22w-32%20md%3Aw-32%20whitespace-nowrap%22%7D"
            label="Novedades"
            isSelected={selectedChip === 'Novedades'}
            onClick={() => setSelectedChip('Novedades')}
            className="w-32 md:w-32 whitespace-nowrap"
          />
          <ChipView data-component-id="src/components/sections/GlutenFreeSection.tsx:133:10" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="133" data-component-file="GlutenFreeSection.tsx" data-component-name="ChipView" data-component-content="%7B%22elementName%22%3A%22ChipView%22%2C%22className%22%3A%22w-40%20md%3Aw-36%20whitespace-nowrap%22%7D"
            label="Promociones"
            isSelected={selectedChip === 'Promociones'}
            onClick={() => setSelectedChip('Promociones')}
            className="w-40 md:w-36 whitespace-nowrap"
          />
          <ChipView data-component-id="src/components/sections/GlutenFreeSection.tsx:139:10" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="139" data-component-file="GlutenFreeSection.tsx" data-component-name="ChipView" data-component-content="%7B%22elementName%22%3A%22ChipView%22%2C%22className%22%3A%22w-36%20md%3Aw-32%20whitespace-nowrap%22%7D"
            label="Sin Gluten"
            isSelected={selectedChip === 'Sin Gluten'}
            onClick={() => setSelectedChip('Sin Gluten')}
            className="w-36 md:w-32 whitespace-nowrap"
          />
        </div>
        {/* Skeleton loader para el carrusel */}
        <div data-component-id="src/components/sections/GlutenFreeSection.tsx:147:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="147" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-4%20overflow-hidden%20px-4%20py-4%22%7D" className="flex gap-4 overflow-hidden px-4 py-4">
          {[...Array(5)].map((_, index) => (
            <div data-component-id="src/components/sections/GlutenFreeSection.tsx:149:12" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="149" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-%5B267px%5D%20h-%5B389px%5D%22%7D" key={index} className="flex-shrink-0 w-[267px] h-[389px]">
              <div data-component-id="src/components/sections/GlutenFreeSection.tsx:150:14" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="150" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20w-full%20h-full%20rounded-%5B17px%5D%20bg-%5B%23FEF5E6%5D%20animate-pulse%22%7D" className="relative w-full h-full rounded-[17px] bg-[#FEF5E6] animate-pulse">

                {/* Product Image Skeleton */}
                <div data-component-id="src/components/sections/GlutenFreeSection.tsx:153:16" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="153" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20w-%5B164px%5D%20h-%5B200px%5D%20top-0%20left-12%20bg-%5B%23E5D4C1%5D%20rounded-lg%22%7D" className="absolute w-[164px] h-[200px] top-0 left-12 bg-[#E5D4C1] rounded-lg"></div>
                
                {/* Product Info Skeleton */}
                <div data-component-id="src/components/sections/GlutenFreeSection.tsx:156:16" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="156" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-%5B253px%5D%20left-%5B21px%5D%20space-y-2%22%7D" className="absolute top-[253px] left-[21px] space-y-2">
                  <div data-component-id="src/components/sections/GlutenFreeSection.tsx:157:18" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="157" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-5%20bg-%5B%23E5D4C1%5D%20rounded%20w-48%22%7D" className="h-5 bg-[#E5D4C1] rounded w-48"></div>
                  <div data-component-id="src/components/sections/GlutenFreeSection.tsx:158:18" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="158" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-%5B%23E5D4C1%5D%20rounded%20w-32%22%7D" className="h-4 bg-[#E5D4C1] rounded w-32"></div>
                  <div data-component-id="src/components/sections/GlutenFreeSection.tsx:159:18" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="159" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-4%20bg-%5B%23E5D4C1%5D%20rounded%20w-24%22%7D" className="h-4 bg-[#E5D4C1] rounded w-24"></div>
                </div>
                
                {/* Price Skeleton */}
                <div data-component-id="src/components/sections/GlutenFreeSection.tsx:163:16" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="163" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-%5B341px%5D%20left-%5B21px%5D%20flex%20items-center%20gap-3%22%7D" className="absolute top-[341px] left-[21px] flex items-center gap-3">
                  <div data-component-id="src/components/sections/GlutenFreeSection.tsx:164:18" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="164" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-%5B%23E5D4C1%5D%20rounded%20w-24%22%7D" className="h-6 bg-[#E5D4C1] rounded w-24"></div>

                </div>

              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const productsToUse = getProductsToShow();

  // Convertir los datos al formato que espera ProductCarousel

  const handleFavoriteToggle = (id: string) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(id)) {
        newFavorites.delete(id);
      } else {
        newFavorites.add(id);
      }
      return newFavorites;
    });
  };

  return (
    <div data-component-id="src/components/sections/GlutenFreeSection.tsx:193:4" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="193" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${className}`}>
      <div data-component-id="src/components/sections/GlutenFreeSection.tsx:194:6" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="194" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-2%20text-left%22%7D" className="mb-2 text-left">
        <h2 data-component-id="src/components/sections/GlutenFreeSection.tsx:195:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="195" data-component-file="GlutenFreeSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-%5B40px%5D%20leading-%5B48px%5D%20text-global-1%20mb-2%22%7D" className="font-tt-norms-extra-black text-[40px] leading-[48px] text-global-1 mb-2">
          {title}
        </h2>
        <div data-component-id="src/components/sections/GlutenFreeSection.tsx:198:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="198" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-global-9%20rounded%20px-4%20inline-block%20mb-6%22%7D" className="bg-global-9 rounded px-4 inline-block mb-6">
          <span data-component-id="src/components/sections/GlutenFreeSection.tsx:199:10" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="199" data-component-file="GlutenFreeSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-%5B22px%5D%20leading-%5B26px%5D%20text-global-1%20whitespace-pre-line%22%7D" className="font-tt-norms-medium text-[22px] leading-[26px] text-global-1 whitespace-pre-line">
            {subtitle}
          </span>
        </div>
      </div>
      {/* Filter Chips */}
      <div data-component-id="src/components/sections/GlutenFreeSection.tsx:205:6" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="205" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20gap-x-4%20mb-4%20justify-start%22%7D" className="flex flex-row gap-x-4 mb-4 justify-start">
        <ChipView data-component-id="src/components/sections/GlutenFreeSection.tsx:206:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="206" data-component-file="GlutenFreeSection.tsx" data-component-name="ChipView" data-component-content="%7B%22elementName%22%3A%22ChipView%22%2C%22className%22%3A%22w-32%20md%3Aw-32%20whitespace-nowrap%22%7D"
          label="Novedades"
          isSelected={selectedChip === 'Novedades'}
          onClick={() => setSelectedChip('Novedades')}
          className="w-32 md:w-32 whitespace-nowrap"
        />
        <ChipView data-component-id="src/components/sections/GlutenFreeSection.tsx:212:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="212" data-component-file="GlutenFreeSection.tsx" data-component-name="ChipView" data-component-content="%7B%22elementName%22%3A%22ChipView%22%2C%22className%22%3A%22w-40%20md%3Aw-36%20whitespace-nowrap%22%7D"
          label="Promociones"
          isSelected={selectedChip === 'Promociones'}
          onClick={() => setSelectedChip('Promociones')}
          className="w-40 md:w-36 whitespace-nowrap"
        />
        <ChipView data-component-id="src/components/sections/GlutenFreeSection.tsx:218:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="218" data-component-file="GlutenFreeSection.tsx" data-component-name="ChipView" data-component-content="%7B%22elementName%22%3A%22ChipView%22%2C%22className%22%3A%22w-36%20md%3Aw-32%20whitespace-nowrap%22%7D"
          label="Sin Gluten"
          isSelected={selectedChip === 'Sin Gluten'}
          onClick={() => setSelectedChip('Sin Gluten')}
          className="w-36 md:w-32 whitespace-nowrap"
        />
      </div>
      {productsToUse && productsToUse.length > 0 ? (
        <ProductCarousel data-component-id="src/components/sections/GlutenFreeSection.tsx:226:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="226" data-component-file="GlutenFreeSection.tsx" data-component-name="ProductCarousel" data-component-content="%7B%22elementName%22%3A%22ProductCarousel%22%2C%22className%22%3A%22mb-8%22%7D"
          breadcrumbMenu={breadcrumbMenu}
          items={productsToUse}
          className="mb-8"
          onFavoriteToggle={handleFavoriteToggle}
          showNavigation={true}
          defaultVisibleCount={4}
        />
      ) : (
        <div data-component-id="src/components/sections/GlutenFreeSection.tsx:235:8" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="235" data-component-file="GlutenFreeSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20gap-x-4%20mb-4%20justify-start%22%7D" className="flex flex-row gap-x-4 mb-4 justify-start">
          <span data-component-id="src/components/sections/GlutenFreeSection.tsx:236:10" data-component-path="src/components/sections/GlutenFreeSection.tsx" data-component-line="236" data-component-file="GlutenFreeSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%20text-%5B22px%5D%20leading-%5B26px%5D%20text-global-1%20whitespace-pre-line%22%2C%22textContent%22%3A%22No%20hay%20productos%20disponibles%22%7D" className="font-tt-norms-medium text-[22px] leading-[26px] text-global-1 whitespace-pre-line">
            No hay productos disponibles
          </span>
        </div>
      )}
    </div>
  );
};

export default GlutenFreeSection;
